import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { subCollectionCreate, subCollectionGetAll, subCollectionGetByParentId, subCollectionGetByParentIdAndSubId, subCollectionRemove, subCollectionUpdate, subCollectionUpdateField } from '../utils/dbUtils';
import { asTimeZone } from '../utils/dateFormatter';
import { collectionGroup, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import moment from "moment-timezone";
import { v4 as uuid } from 'uuid';
import { isValid3LetterCountryCode, isValid5or9ZipCode, isValidStateAbbreviation } from '../utils/validationUtils';
import { toE164PhoneNumber } from '../utils/stringFormatter';

class UserAddress {
	static collectionName = 'users';
	static subCollectionName = 'addresses';

	id = uuid();
	userId = null;

	firstName = null;
	lastName = null;
	company = null;
	streetAddress1 = null;
	streetAddress2 = null;
	city = null;
	state = null;
	postalCode = null;
	country = null;
	phone = null;
	verificationStatus = null; // enum AddressVerificationStatuses

	isDefault = false;

	constructor(userAddress) {
		if (isObjectNotEmpty(userAddress)) {
			if (isStringNotEmpty(userAddress.id)) { this.id = userAddress.id; }
			if (isStringNotEmpty(userAddress.userId)) { this.userId = userAddress.userId; }

			if (isStringNotEmpty(userAddress.firstName)) { this.firstName = userAddress.firstName.trim(); }
			if (isStringNotEmpty(userAddress.lastName)) { this.lastName = userAddress.lastName.trim(); }
			if (isStringNotEmpty(userAddress.company)) { this.company = userAddress.company.trim(); }
			if (isStringNotEmpty(userAddress.streetAddress1)) { this.streetAddress1 = userAddress.streetAddress1.trim(); }
			if (isStringNotEmpty(userAddress.streetAddress2)) { this.streetAddress2 = userAddress.streetAddress2.trim(); }
			if (isStringNotEmpty(userAddress.city)) { this.city = userAddress.city.trim(); }
			if (isStringNotEmpty(userAddress.state)) { this.state = userAddress.state; }
			if (isStringNotEmpty(userAddress.postalCode)) { this.postalCode = userAddress.postalCode.trim(); }
			if (isStringNotEmpty(userAddress.country)) { this.country = userAddress.country; }

			if (isStringNotEmpty(userAddress.phone)) { this.phone = toE164PhoneNumber(userAddress.phone); }
			if (isStringNotEmpty(userAddress.verificationStatus)) { this.verificationStatus = userAddress.verificationStatus; }

			if (isBooleanTrue(userAddress.isDefault)) { this.isDefault = true; }
		}
	}

	toDto = () => {
		return {
			id: this.id,
			userId: this.userId,

			firstName: this.firstName,
			lastName: this.lastName,
			company: this.company,
			streetAddress1: this.streetAddress1,
			streetAddress2: this.streetAddress2,
			city: this.city,
			state: this.state,
			postalCode: this.postalCode,
			country: this.country,
			phone: this.phone,
			verificationStatus: this.verificationStatus,

			isDefault: this.isDefault
		};
	}

	isValid = () => {
		if (isStringEmpty(this.streetAddress1)) {
			throw new Error('Street Address Line 1 is required.');
		}
		if (isStringEmpty(this.city)) {
			throw new Error('City is required.');
		}
		if (isStringEmpty(this.state) || !isValidStateAbbreviation(this.state)) {
			throw new Error('State is required and must be a valid 2 letter state abbreviation.');
		}
		if (isStringEmpty(this.postalCode) || !isValid5or9ZipCode(this.postalCode)) {
			throw new Error('Postal code is required and must be a valid 5 digit or 9 digit postal code.');
		}
		if (isStringEmpty(this.country) || !isValid3LetterCountryCode(this.country)) {
			throw new Error('Country is required and must be a valid 3 letter country code.');
		}

		return true;
	}

	create = async () => {
		if (isBooleanTrue(this.isDefault)) {
			let otherAddresses = await UserAddress.getByUserId(this.userId);
			for (const otherAddress of otherAddresses) {
				await UserAddress.updateField(this.userId, otherAddress.id, 'isDefault', false);
			}
		}

		return await subCollectionCreate(UserAddress.collectionName, this.userId, UserAddress.subCollectionName, this.id, this.toDto());
	}

	static setAsDefault = async (userId, userAddressId) => {
		let userAddresses = await UserAddress.getByUserId(userId);
		for (const otherAddress of userAddresses) {
			if (otherAddress.id !== userAddressId) {
				await UserAddress.updateField(userId, otherAddress.id, 'isDefault', false);
			}
		}

		return await UserAddress.updateField(userId, userAddressId, 'isDefault', true);
	}

	static update = async (userId, userAddressId, updatedUserAddress) => {
		const updatedObj = new UserAddress(updatedUserAddress);

		if (isBooleanTrue(updatedObj.isDefault)) {
			let userAddresses = await UserAddress.getByUserId(userId);
			for (const otherAddress of userAddresses) {
				if (otherAddress.id !== userAddressId) {
					await UserAddress.updateField(userId, otherAddress.id, 'isDefault', false);
				}
			}
		}

		return await subCollectionUpdate(UserAddress.collectionName, userId, UserAddress.subCollectionName, userAddressId, updatedObj.toDto());
	}

	static updateField = async (userId, userAddressId, fieldName, newValue) => {
		if (fieldName === 'isDefault' && isBooleanTrue(newValue)) {
			let otherAddresses = await UserAddress.getByUserId(userId);
			for (const otherAddress of otherAddresses) {
				await UserAddress.updateField(userId, otherAddress.id, 'isDefault', false);
			}
		}

		return await subCollectionUpdateField(UserAddress.collectionName, userId, UserAddress.subCollectionName, userAddressId, fieldName, newValue);
	}

	static delete = async (userId, userAddressId) => {
		return await subCollectionRemove(UserAddress.collectionName, userId, UserAddress.subCollectionName, userAddressId);
	}

	static getByUserIdAndUserAddressId = async (userId, userAddressId) => {
		return await subCollectionGetByParentIdAndSubId(UserAddress.collectionName, userId, UserAddress.subCollectionName, userAddressId);
	}

	static getAll = async () => {
		return await subCollectionGetAll(UserAddress.subCollectionName);
	}

	static getByUserId = async (userId) => {
		return await subCollectionGetByParentId(UserAddress.collectionName, userId, UserAddress.subCollectionName);
	}

	static getCount = async (queryParameters) => {
		let { userId, isDefault, start, end } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isBooleanTrue(isDefault)) {
			queryConstraints.push(where('isDefault', '==', true));
		} else if (isBooleanFalse(isDefault)) {
			queryConstraints.push(where('isDefault', '==', false));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = query(collectionGroup(db, UserAddress.subCollectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
		let { userId, isDefault, start, end } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(userId)) {
			queryConstraints.push(where('userId', 'in', userId));
		} else if (isStringNotEmptyOrZeroLength(userId)) {
			queryConstraints.push(where('userId', '==', userId));
		}

		if (isBooleanTrue(isDefault)) {
			queryConstraints.push(where('isDefault', '==', true));
		} else if (isBooleanFalse(isDefault)) {
			queryConstraints.push(where('isDefault', '==', false));
		}

		if (isNotNullOrUndefined(start)) {
			let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '>=', startDate));
		}

		if (isNotNullOrUndefined(end)) {
			let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
			queryConstraints.push(where('createdAt', '<=', endDate));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collectionGroup(db, UserAddress.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField]));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collectionGroup(db, UserAddress.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), limit(pageSize), startAfter(lastVisible[orderByField]));
		} else {
			q = query(collectionGroup(db, UserAddress.subCollectionName), ...queryConstraints, orderBy(orderByField, 'asc'), limit(pageSize));
		}

		let snapShot = await getDocs(q);
		return snapShot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;
			let totalPages = 0;

			let totalCount = await this.getCount(queryParameters);
			totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default UserAddress;